import React, { Fragment } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  CELEBRATION_ACCENT_BAR,
  CELEBRATION_BODY,
  CELEBRATION_CLOSE,
  CELEBRATION_CONTEXT_LABEL,
  CELEBRATION_EYEBROW,
  CELEBRATION_HEADER,
  CELEBRATION_META,
  CELEBRATION_OVERLAY,
  CELEBRATION_PANEL,
  CELEBRATION_PRIMARY_CTA,
  CELEBRATION_SECONDARY_CTA,
  CELEBRATION_TITLE,
  CELEBRATION_XP_CHIP,
  type CelebrationPersona,
} from './celebration.tokens';
import type { EnrichedCelebration } from './celebration.types';
import { CelebrationNextStep } from './CelebrationNextStep';

type Props = {
  open: boolean;
  item: EnrichedCelebration | null;
  persona: CelebrationPersona;
  icon: React.ReactNode;
  testId?: string;
  onClose: () => void;
};

/** Tier 3 — rare peak moments; soft card, focused moment. */
export const CelebrationModal: React.FC<Props> = ({
  open,
  item,
  persona,
  icon,
  testId = 'celebration-tier3-modal',
  onClose,
}) => {
  const navigate = useNavigate();

  if (!item) return null;

  const goPrimary = () => {
    onClose();
    navigate(item.primaryCtaHref);
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog className="relative z-[200]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-250"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 ${CELEBRATION_OVERLAY} pointer-events-none`}
            aria-hidden
          />
        </TransitionChild>

        <div className="pointer-events-none fixed inset-0 z-[201] flex items-end justify-center p-4 sm:items-center">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-250"
            enterFrom="opacity-0 translate-y-3 sm:translate-y-1 sm:scale-[0.98]"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-3 sm:translate-y-1 sm:scale-[0.98]"
          >
            <DialogPanel
              className={`pointer-events-auto relative w-full max-w-sm sm:max-w-md ${CELEBRATION_PANEL[persona]}`}
              data-testid={testId}
            >
              <div className={CELEBRATION_ACCENT_BAR[persona]} aria-hidden />

              <div className={CELEBRATION_HEADER[persona]}>
                <button
                  type="button"
                  onClick={onClose}
                  className={`absolute right-3 top-3 ${CELEBRATION_CLOSE}`}
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                  {CELEBRATION_CONTEXT_LABEL[persona]}
                </p>

                <div className="mx-auto mt-4 flex justify-center">{icon}</div>

                <p className={`mt-4 ${CELEBRATION_EYEBROW[persona]}`}>{item.eyebrow}</p>
                <h2 className={CELEBRATION_TITLE}>{item.title}</h2>

                {item.xp && item.xp > 0 ? (
                  <span
                    className={`mt-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${CELEBRATION_XP_CHIP[persona]}`}
                  >
                    +{item.xp} activity points
                  </span>
                ) : null}
              </div>

              <div className="px-6 py-5">
                <p className={CELEBRATION_BODY}>{item.whyItMatters}</p>
                <p className="mt-2 text-center text-xs text-slate-500">{item.description}</p>

                {item.unlockedAt ? (
                  <p className={CELEBRATION_META}>
                    {new Date(item.unlockedAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                ) : null}

                <CelebrationNextStep item={item} persona={persona} className="mt-4" />

                <div className="mt-5 flex flex-col gap-2">
                  <button type="button" onClick={goPrimary} className={CELEBRATION_PRIMARY_CTA[persona]}>
                    {item.primaryCtaLabel}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className={CELEBRATION_SECONDARY_CTA[persona]}
                  >
                    {item.secondaryCtaLabel}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
